import type { TaxResults, TaxScenarioInput } from '@/tax/types';
import { formatCurrency, formatPercent, formatChartCurrency } from '@/tax/format';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GitBranch } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface ResultsDisplayProps {
  results: TaxResults;
  /** The inputs behind these results — drives the Taxes Already Paid block. Optional so
   *  older call sites and result-only renders still work (the block simply hides). */
  input?: TaxScenarioInput;
  scenarioName: string;
  onCreateScenario?: () => void;
  onBack?: () => void;
}

function ResultRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`flex justify-between py-2 px-3 rounded-md text-sm transition-colors ${highlight ? 'bg-primary/8 font-semibold border border-primary/15' : 'hover:bg-muted/50'}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono text-xs ${highlight ? 'text-primary' : 'text-foreground'}`}>{value}</span>
    </div>
  );
}

function SectionDivider({ title, tour }: { title: string; tour?: string }) {
  return (
    <div className="pt-4 pb-1.5" data-tour={tour}>
      <p className="text-[11px] font-bold uppercase tracking-widest text-primary/60">{title}</p>
      <div className="h-px bg-gradient-to-r from-primary/20 to-transparent mt-1.5" />
    </div>
  );
}

function MetricCard({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div className={`relative overflow-hidden rounded-xl border p-4 text-center ${accent}`}>
      <div className="absolute inset-0 opacity-[0.04] bg-gradient-to-br from-current to-transparent" />
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-1">{label}</p>
      <p className="text-xl font-bold font-mono">{value}</p>
    </div>
  );
}

const CHART_COLORS = [
  'hsl(var(--chart-federal))',
  'hsl(var(--chart-state))',
  'hsl(var(--chart-fica))',
  'hsl(var(--chart-amt))',
  'hsl(var(--chart-niit))',
  'hsl(var(--chart-medicare))',
];

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card px-3 py-2 shadow-lg text-xs">
      <p className="font-semibold">{payload[0].name}</p>
      <p className="font-mono text-primary">{formatCurrency(payload[0].value)}</p>
    </div>
  );
};

export default function ResultsDisplay({ results, input, scenarioName, onCreateScenario, onBack }: ResultsDisplayProps) {
  // Rows saved before stateWithholding existed carry neither the input nor the engine result.
  const stateWithheld = input?.stateWithholding ?? 0;
  const netState = results.netStateTaxOwedOrRefund ?? results.stateIncomeTax - stateWithheld;
  // Build chart data - only include non-zero items
  const pieData = [
    { name: 'Ordinary Income Tax', value: results.ordinaryIncomeTax },
    { name: 'CG/Div Tax', value: results.capitalGainsDividendsTax },
    { name: 'State Tax', value: results.stateIncomeTax },
    { name: 'SE Tax', value: results.selfEmploymentTax },
    { name: 'AMT', value: results.amtOwed },
    { name: 'NIIT', value: results.niit },
    { name: 'Addl Medicare', value: results.additionalMedicareTax },
  ].filter(d => d.value > 0);

  const barData = [
    { name: 'Total Income', value: results.totalIncome },
    { name: 'AGI', value: results.adjustedGrossIncome },
    { name: 'Taxable', value: results.taxableIncome },
    { name: 'Fed Tax', value: results.totalFederalTax },
    { name: 'State Tax', value: results.stateIncomeTax },
    { name: 'Total Tax', value: results.totalTaxLiability },
  ];

  const hasTaxData = results.totalTaxLiability > 0;

  return (
    <div className="space-y-4" data-tour="results-panel">
      <Card className="shadow-lg border-0 bg-gradient-to-br from-card to-card/80">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">{scenarioName}</CardTitle>
              <p className="text-xs text-muted-foreground">Tax Calculation Results</p>
            </div>
            {onCreateScenario && (
              <Button variant="outline" size="sm" onClick={onCreateScenario} data-tour="results-create-scenario">
                <GitBranch className="h-4 w-4 mr-1" /> Create Strategy
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-1">
          {/* AMT Alert */}
          {results.amtOwed > 0 && (
            <div className="rounded-xl border-2 border-destructive bg-destructive/10 p-4 flex items-center gap-3 mb-2">
              <span className="text-3xl">⚠️</span>
              <div>
                <p className="font-bold text-destructive text-base">AMT Triggered</p>
                <p className="text-sm text-destructive/80">
                  Alternative Minimum Tax of {formatCurrency(results.amtOwed)} applies to this scenario.
                </p>
              </div>
            </div>
          )}

          {/* NIIT Alert */}
          {results.niit > 0 && (
            <div className="rounded-xl border-2 border-amber-500/50 bg-amber-500/10 p-4 flex items-center gap-3 mb-2">
              <span className="text-3xl">⚠️</span>
              <div>
                <p className="font-bold text-amber-700 dark:text-amber-400 text-base">NIIT Applies</p>
                <p className="text-sm text-amber-700/80 dark:text-amber-400/80">
                  Net Investment Income Tax of {formatCurrency(results.niit)} applies to this scenario.
                </p>
              </div>
            </div>
          )}

          {/* Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pb-4" data-tour="results-metrics">
            <MetricCard label="Total Tax" value={formatCurrency(results.totalTaxLiability)} accent="text-primary" />
            <MetricCard label="Effective Rate" value={formatPercent(results.effectiveTaxRate)} accent="text-secondary" />
            <MetricCard label="Economic Cost" value={formatCurrency(results.totalEconomicCost)} accent="text-destructive" />
          </div>

          {/* Charts */}
          {hasTaxData && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-4" data-tour="results-charts">
              {/* Pie Chart - Tax Breakdown */}
              <Card className="border shadow-none">
                <CardHeader className="pb-0 pt-4 px-4">
                  <CardTitle className="text-xs uppercase tracking-widest text-muted-foreground">Tax Composition</CardTitle>
                </CardHeader>
                <CardContent className="p-2">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={75}
                        paddingAngle={3}
                        dataKey="value"
                        strokeWidth={0}
                      >
                        {pieData.map((_, index) => (
                          <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        iconSize={8}
                        wrapperStyle={{ fontSize: '12px' }}
                        formatter={(value: string) => <span className="text-muted-foreground">{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Bar Chart - Income Waterfall */}
              <Card className="border shadow-none">
                <CardHeader className="pb-0 pt-4 px-4">
                  <CardTitle className="text-xs uppercase tracking-widest text-muted-foreground">Income → Tax Waterfall</CardTitle>
                </CardHeader>
                <CardContent className="p-2">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={barData} barSize={20} margin={{ top: 10, right: 5, bottom: 5, left: 5 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={formatChartCurrency} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {barData.map((_, index) => (
                          <Cell
                            key={index}
                            fill={index < 3 ? 'hsl(var(--chart-federal))' : index < 5 ? 'hsl(var(--chart-state))' : 'hsl(var(--destructive))'}
                            opacity={index < 3 ? 1 - index * 0.15 : 1}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}

          <SectionDivider title="Federal" tour="results-federal" />
          <ResultRow label="AGI" value={formatCurrency(results.adjustedGrossIncome)} />
          <ResultRow label="Deduction Used" value={formatCurrency(results.totalDeduction)} />
          <ResultRow label="Taxable Income" value={formatCurrency(results.taxableIncome)} />
          <ResultRow label="Itemized Deductions" value={formatCurrency(results.itemizedDeductions)} />
          <ResultRow label="Standard Deduction" value={formatCurrency(results.standardDeduction)} />
          <ResultRow label="BBB Amount" value={formatCurrency(results.bbbAmount)} />
          <ResultRow label="QBI Deduction" value={formatCurrency(results.qbiDeduction)} />
          <ResultRow label="Taxable Social Security" value={formatCurrency(results.taxableSocialSecurity)} />
          <ResultRow label="Ordinary Income Tax" value={formatCurrency(results.ordinaryIncomeTax)} />
          {results.capitalGainsDividendsTax > 0 && (
            <ResultRow label="Capital Gains/Dividends Tax" value={formatCurrency(results.capitalGainsDividendsTax)} />
          )}
          {results.amtOwed > 0 && (
            <ResultRow label="AMT Owed" value={formatCurrency(results.amtOwed)} />
          )}
          {results.niit > 0 && (
            <ResultRow label="NIIT" value={formatCurrency(results.niit)} />
          )}
          <ResultRow label="Additional Medicare Tax" value={formatCurrency(results.additionalMedicareTax)} />
          {results.selfEmploymentTax > 0 && (
            <ResultRow label="Self-Employment Tax" value={formatCurrency(results.selfEmploymentTax)} />
          )}
          <ResultRow label="Total Federal Tax" value={formatCurrency(results.totalFederalTax)} highlight />
          {/* Payments sit under the total they come off, so each side subtracts top-to-bottom. */}
          {input && input.federalWithholding > 0 && (
            <ResultRow label="Federal Withholding" value={formatCurrency(input.federalWithholding)} />
          )}
          {input && input.estimatedTaxPayments > 0 && (
            <ResultRow label="Federal Estimated Payments" value={formatCurrency(input.estimatedTaxPayments)} />
          )}
          {input && input.amountPaidWithExtension > 0 && (
            <ResultRow label="Paid with Extension" value={formatCurrency(input.amountPaidWithExtension)} />
          )}
          <ResultRow label={results.netTaxOwedOrRefund >= 0 ? "Federal Tax Owed" : "Federal Refund Due"} value={formatCurrency(Math.abs(results.netTaxOwedOrRefund))} highlight />

          <SectionDivider title="State" tour="results-state" />
          <ResultRow label="State Taxable Income" value={formatCurrency(results.stateTaxableIncome)} />
          <ResultRow label="State Income Tax" value={formatCurrency(results.stateIncomeTax)} highlight />
          {stateWithheld > 0 && (
            <ResultRow label="State Tax Withheld" value={formatCurrency(stateWithheld)} />
          )}
          <ResultRow label={netState >= 0 ? "Net State Tax Owed" : "State Refund Due"} value={formatCurrency(Math.abs(netState))} highlight />

          <SectionDivider title="Combined" tour="results-combined" />
          <ResultRow label="Total Tax (Fed + State)" value={formatCurrency(results.totalTaxLiability)} highlight />
          <ResultRow label="Effective Tax Rate" value={formatPercent(results.effectiveTaxRate)} highlight />

          {results.totalMedicarePremium > 0 && (
            <>
              <SectionDivider title="Medicare IRMAA" />
              <ResultRow label="Part B Annual Premium" value={formatCurrency(results.medicarePartBPremium * 12)} />
              <ResultRow label="Part D Annual Surcharge" value={formatCurrency(results.medicarePartDPremium * 12)} />
              <ResultRow label="Total Annual Premium" value={formatCurrency(results.totalMedicarePremium * 12)} />
            </>
          )}

          {results.alternativeInvestmentCapital > 0 && (
            <>
              <SectionDivider title="Economic Cost" />
              <ResultRow label="Alternative Investment Capital" value={formatCurrency(results.alternativeInvestmentCapital)} />
              <ResultRow label="Total Economic Cost" value={formatCurrency(results.totalEconomicCost)} highlight />
            </>
          )}

          <SectionDivider title="Roth Conversion Growth" />
          <ResultRow label="Roth Future Value" value={formatCurrency(results.rothFutureValue)} />
          <ResultRow label="Est. Future Tax Avoided" value={formatCurrency(results.estimatedFutureTaxAvoided)} highlight />

        </CardContent>
      </Card>
    </div>
  );
}
